import {
    cancelBacktestJob,
    createBacktestSpec,
    deleteBacktestJob,
    deleteBacktestSpec,
    executeBacktest,
    getBacktestComplexityEstimate,
    getBacktestJobById,
    getBacktestSpecById,
    getBacktestSpecs,
    getBacktestUniverseSize,
    updateBacktestSpec,
} from './http/backtestSpecs';
import type { BacktestJobWithResults } from './http/backtestSpecs';
import type { MutationObserverOptions, QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type {
    BacktestComplexityEstimate,
    BacktestComplexityEstimateRequest,
    BacktestExecuteRequest,
    BacktestJob,
    BacktestJobRecord,
    BacktestSpec,
    BacktestSpecPatch,
    BacktestUniverseSize,
    BacktestUniverseSizeRequest,
    NewBacktestSpec,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';

export { BacktestJobWithResultsSchema } from './http/backtestSpecs';
export type { BacktestJobWithResults } from './http/backtestSpecs';

export const BACKTEST_SPECS = 'backtest-specs';
export const BACKTEST_JOBS = 'backtest-jobs';
export const BACKTEST_UNIVERSE_SIZE = 'backtest-universe-size';
export const BACKTEST_COMPLEXITY_ESTIMATE = 'backtest-complexity-estimate';

/**
 * Query options for listing backtest specs, with optional search.
 */
export function getBacktestSpecsQuery(search?: string): QueryObserverOptions<BacktestSpec[]> {
    return {
        queryKey: [BACKTEST_SPECS, { search }],
        queryFn: () => {
            const params: Record<string, string> = {};
            if (search) {
                params['$search'] = search;
            }
            return getBacktestSpecs(params);
        },
        staleTime: 1000 * 60 * 5,
    };
}

/**
 * Query options for fetching a single backtest spec by ID.
 */
export function getBacktestSpecByIdQuery(id: string | null): QueryObserverOptions<BacktestSpec | null> {
    return {
        queryKey: [BACKTEST_SPECS, id],
        queryFn: () => (id ? getBacktestSpecById(id) : Promise.resolve(null)),
        enabled: Boolean(id),
    };
}

export function getCreateBacktestSpecMutation(
    queryClient: QueryClient,
): MutationObserverOptions<BacktestSpec, Error, NewBacktestSpec> {
    return {
        mutationFn: createBacktestSpec,
        onSuccess: () => {
            invalidateBacktestSpecsQueries(queryClient);
        },
    };
}

export function getUpdateBacktestSpecMutation(
    queryClient: QueryClient,
): MutationObserverOptions<BacktestSpec, Error, { id: string; patch: BacktestSpecPatch }> {
    return {
        mutationFn: ({ id, patch }) => updateBacktestSpec(id, patch),
        onSuccess: () => {
            invalidateBacktestSpecsQueries(queryClient);
        },
    };
}

interface OptimisticDeleteContext {
    previousSpecsByQuery: [queryKey: readonly unknown[], data: BacktestSpec[] | undefined][];
}

/**
 * Mutation options for deleting a backtest spec, with optimistic removal from
 * the list cache and rollback on failure.
 */
export function getDeleteBacktestSpecMutation(
    queryClient: QueryClient,
): MutationObserverOptions<void, Error, string, OptimisticDeleteContext> {
    return {
        mutationFn: deleteBacktestSpec,
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey: [BACKTEST_SPECS] });
            const previousSpecsByQuery = queryClient.getQueriesData<BacktestSpec[]>({
                queryKey: [BACKTEST_SPECS],
            });
            queryClient.setQueriesData<BacktestSpec[]>({ queryKey: [BACKTEST_SPECS] }, (old) =>
                Array.isArray(old) ? old.filter((spec) => spec.id !== id) : old,
            );
            return { previousSpecsByQuery };
        },
        onError: (_err, _id, context) => {
            if (!context) return;
            for (const [queryKey, data] of context.previousSpecsByQuery) {
                queryClient.setQueryData(queryKey, data);
            }
        },
        onSuccess: (_data, id) => {
            queryClient.removeQueries({ queryKey: [BACKTEST_SPECS, id] });
        },
        onSettled: () => {
            invalidateBacktestSpecsQueries(queryClient);
        },
    };
}

export function invalidateBacktestSpecsQueries(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ queryKey: [BACKTEST_SPECS] });
}

/**
 * Query options for fetching a single backtest job by ID.
 */
export function getBacktestJobByIdQuery(id: string): QueryObserverOptions<BacktestJobWithResults | null> {
    return {
        queryKey: [BACKTEST_JOBS, id],
        queryFn: () => getBacktestJobById(id),
    };
}

/**
 * Mutation options for executing a backtest. Seeds the returned job into the
 * single-job cache so the polling reader picks it up without an extra GET.
 */
export function getExecuteBacktestMutation(
    queryClient: QueryClient,
): MutationObserverOptions<BacktestJob, Error, BacktestExecuteRequest> {
    return {
        mutationFn: executeBacktest,
        onSuccess: (job) => {
            queryClient.setQueryData([BACKTEST_JOBS, job.id], job);
        },
    };
}

/**
 * Mutation options for deleting a backtest job. Optimistic state for the jobs
 * list itself is owned by `BacktestJobsListModel` (the list lives in model
 * state, not in the query cache); this mutation only handles the per-job cache
 * cleanup.
 */
export function getDeleteBacktestJobMutation(queryClient: QueryClient): MutationObserverOptions<void, Error, string> {
    return {
        mutationFn: deleteBacktestJob,
        onSuccess: (_data, id) => {
            queryClient.removeQueries({ queryKey: [BACKTEST_JOBS, id] });
        },
    };
}

/**
 * Mutation options for cancelling a running backtest job. The backend returns
 * the updated `BacktestJobRecord` (status `'cancelled'` on a successful
 * cancellation, or unchanged if the engine reports the job was already
 * terminal). Optimistic state for the jobs list itself is owned by
 * `BacktestJobsListModel`; this mutation only refreshes the per-job cache.
 */
export function getCancelBacktestJobMutation(
    queryClient: QueryClient,
): MutationObserverOptions<BacktestJobRecord, Error, string> {
    return {
        mutationFn: cancelBacktestJob,
        onSuccess: (job) => {
            queryClient.setQueryData([BACKTEST_JOBS, job.id], job);
        },
    };
}

/**
 * Query options for previewing how many securities a backtest universe will
 * match. Passing `null` short-circuits via `enabled: false` so callers can pass
 * the latest request shape directly from a signal without branching.
 */
export function getBacktestUniverseSizeQuery(
    request: BacktestUniverseSizeRequest | null,
): QueryObserverOptions<BacktestUniverseSize | null> {
    return {
        queryKey: [BACKTEST_UNIVERSE_SIZE, request],
        queryFn: () => (request ? getBacktestUniverseSize(request) : Promise.resolve(null)),
        enabled: request != null,
        staleTime: 1000 * 30,
    };
}

/**
 * Query options for estimating a backtest's runtime complexity. Passing `null`
 * short-circuits via `enabled: false` so callers can pass the latest request
 * shape directly from a signal without branching.
 */
export function getBacktestComplexityEstimateQuery(
    request: BacktestComplexityEstimateRequest | null,
): QueryObserverOptions<BacktestComplexityEstimate | null> {
    return {
        queryKey: [BACKTEST_COMPLEXITY_ESTIMATE, request],
        queryFn: () => (request ? getBacktestComplexityEstimate(request) : Promise.resolve(null)),
        enabled: request != null,
        staleTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    };
}
